import { useTranslation } from "react-i18next";

import {
  queryOptions,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { epochToDate } from "@/domain/case/map-process-instance";
import { notifySuccess } from "@/lib/notify";

/**
 * 外部連携の鍵 1 件。
 *
 * 値は一覧 API が返さない（機微なので backend が伏せる）。画面でも表示しない。
 */
export type SecretSummary = {
  readonly id: number;
  readonly key: string;
  readonly owner: string | null;
  readonly updatedAt: Date | null;
};

type SpiffSecretRow = {
  readonly id: number;
  readonly key: string;
  readonly username?: string | null;
  readonly updated_at_in_seconds?: number | null;
};

const QUERY_KEY = ["secrets"] as const;

export function secretsQueryOptions(client: ApiClient) {
  const fetchSecrets = async (): Promise<readonly SecretSummary[]> => {
    const response = await client.get<{
      readonly results?: readonly SpiffSecretRow[];
    }>("/secrets", { query: { per_page: 100, page: 1 } });

    return (
      (response.results ?? [])
        .map<SecretSummary>((row) => ({
          id: row.id,
          key: row.key,
          owner: row.username ?? null,
          updatedAt: epochToDate(row.updated_at_in_seconds),
        }))
        // キー名で探すので昇順。ASCII の記号込みで安定させるため localeCompare は使わない
        .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
    );
  };

  return queryOptions({
    queryKey: QUERY_KEY,
    queryFn: fetchSecrets,
    staleTime: 60_000,
  });
}

export function useSecrets(): UseQueryResult<readonly SecretSummary[]> {
  return useQuery(secretsQueryOptions(useApiClient()));
}

/**
 * 個別の鍵の URL。鍵名はパスに入るのでエンコードする（`/` や `?` を含む鍵が
 * 別のパス・クエリとして解釈されないように）。
 */
export function secretPath(key: string): string {
  return `/secrets/${encodeURIComponent(key)}`;
}

/** 鍵の追加・更新。値はここでしか送らない（画面に表示して戻すことはしない）。 */
export function useUpsertSecret(): UseMutationResult<
  unknown,
  Error,
  { readonly key: string; readonly value: string; readonly existingId?: number }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: ({ key, value, existingId }) =>
      existingId === undefined
        ? client.post("/secrets", { key, value })
        : client.put(secretPath(key), { key, value }),
    onSuccess: () => {
      // 値は画面に出さないので、通ったことは表示でしか分からない
      notifySuccess(t("toast.secretSaved"));
      return queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteSecret(): UseMutationResult<unknown, Error, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (key: string) => client.del(secretPath(key)),
    onSuccess: () => {
      notifySuccess(t("toast.secretRemoved"));
      return queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
